import React, { useState } from 'react';
import { Grid, Typography, Card, CardHeader, CardContent } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIntlBoard } from '../../../endpoints/chapters';
import { BoardOfDirectorsResponse } from '../../../endpoints/chaptersTs';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import { getGroupRolesByType } from '../../../endpoints/groups';
import {
  INTL_GROUP_TYPE_EXECUTIVE_BOARD,
  IntlSaveData,
  saveIntlLeadershipPosition,
} from './intlLeadershipEdit';
import AlertItem from '../../../Components/AlertItem';

const IntlBoard: React.FC = () => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);

  const { data: boardData, isFetching } = useApiGet<BoardOfDirectorsResponse>(
    ['intl-board'],
    getIntlBoard,
    {
      enabled: true,
      retry: 1,
    },
    {}
  );

  const { data: boardRoles } = useApiGet(
    ['intl-board-roles'],
    () => getGroupRolesByType(INTL_GROUP_TYPE_EXECUTIVE_BOARD),
    { enabled: Boolean(boardData?.can_edit), retry: 1 },
    {}
  );

  const canEdit = Boolean(boardData?.can_edit);

  const onSavePosition = (position: IntlSaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    void saveIntlLeadershipPosition(position)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['intl-board'] });
      })
      .catch((err: { message?: string }) => {
        setErrorSaving(err?.message || 'Failed to save position.');
      })
      .finally(() => setIsSaving(false));
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!boardData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      {errorSaving && (
        <Grid item xs={12} sx={{ px: 2, pt: 2 }}>
          <AlertItem
            severity="error"
            description={errorSaving}
            onClose={() => setErrorSaving(null)}
          />
        </Grid>
      )}
      <Grid item xs={12} sx={{ padding: 2 }}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={boardData.name}
            subheader={boardData.description}
            sx={{ p: 1 }}
          />
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2}>
              {(boardData.members || [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((user) => (
                  <PositionDisplay
                    id={user.id}
                    position={user.position || ''}
                    positionId={user.position_id || ''}
                    committeeId={
                      user.committee_id || user.group_id || boardData.id
                    }
                    name={user.name}
                    startDate={user.start_date}
                    endDate={user.end_date}
                    canEdit={canEdit}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={canEdit ? onSavePosition : null}
                    userId={user.user_id}
                    key={user.id}
                    pageFrom={'intl-board'}
                    canMessage={true}
                  />
                ))}
              {canEdit && (
                <Grid item xs={12} sx={{ p: 1 }}>
                  <AddPosition
                    buttonText="Add Position"
                    committeeId={boardData.id}
                    positions={(boardRoles || []).map((r: any) => ({
                      id: r.id,
                      name: r.name,
                      order: r.order,
                    }))}
                    onAdd={onSavePosition}
                    isSaving={isSaving}
                    savingError={errorSaving}
                    saveSuccess={null}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default IntlBoard;
